import { apiUrl } from "../taskpane";

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    let message = "Request failed";
    if (data?.detail) {
      if (typeof data.detail === "string") {
        message = data.detail;
      } else {
        try {
          message = JSON.stringify(data.detail);
        } catch (error) {
          message = "Request failed";
        }
      }
    }
    const error = new Error(message);
    error.status = response.status;
    error.body = data;
    throw error;
  }
  return data;
};

export async function fetchProductTables(token) {
  const res = await fetch(apiUrl("product-tables"), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function createProductTable(token, payload) {
  const res = await fetch(apiUrl("product-tables"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function fetchProductTableRows(token, tableId) {
  const res = await fetch(apiUrl(`product-tables/${tableId}/rows`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function replaceProductTableRows(token, tableId, records) {
  const res = await fetch(apiUrl(`product-tables/${tableId}/rows`), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ records }),
  });
  return parseResponse(res);
}
// Tier 1 functions
export async function fetchProductTableRowsTier1(token, tableId) {
  const res = await fetch(apiUrl(`product-tables/${tableId}/rows-tier1`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function replaceProductTableRowsTier1(token, tableId, records) {
  const res = await fetch(apiUrl(`product-tables/${tableId}/rows-tier1`), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ records }),
  });
  return parseResponse(res);
}

export async function fetchChildTableTotals(token, tableId) {
  const res = await fetch(apiUrl(`product-tables/${tableId}/child-totals`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function fetchProductTable(token, tableId) {
  const res = await fetch(apiUrl(`product-tables/${tableId}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function ensureProjectBoqRoot(token, projectId, baseName) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/root`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ base_name: baseName || "BOQ" }),
  });
  return parseResponse(res);
}

export async function createOrGetChildBoqTable(token, parentTableId, rowId, baseName) {
  const res = await fetch(apiUrl(`product-tables/${parentTableId}/rows/${rowId}/child-table`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ base_name: baseName || "BOQ" }),
  });
  return parseResponse(res);
}

export async function searchMaterials(token, query, limit = 20, resourceType = "", tableFormat = "", tableId = "", lang = "") {
  const params = new URLSearchParams({ q: query, limit: limit.toString() });
  if (resourceType) params.set("resource_type", resourceType);
  if (tableFormat) params.set("table_format", tableFormat);
  if (tableId) params.set("table_id", tableId);
  if (lang) params.set("lang", lang);
  const res = await fetch(apiUrl(`materials/search?${params}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

// ═══ Catalog Standards API ═══

export async function fetchCatalogStandards(token) {
  const res = await fetch(apiUrl("catalogs"), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function fetchCatalogGroups(token, catalogId, search = "") {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  const res = await fetch(apiUrl(`catalogs/${catalogId}/groups${params}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function fetchGroupVariants(token, catalogId, groupId) {
  const res = await fetch(apiUrl(`catalogs/${catalogId}/groups/${groupId}/variants`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function fetchVariantResources(token, variantId) {
  const res = await fetch(apiUrl(`catalogs/variants/${variantId}/resources`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}


// ═══ BOQ Versions ═══

export async function fetchBoqVersions(token, projectId) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/versions`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function createBoqVersion(token, projectId, { version_name, base_name, set_as_primary } = {}) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/versions`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ version_name, base_name, set_as_primary }),
  });
  return parseResponse(res);
}

export async function updateBoqVersion(token, projectId, versionId, { version_name, set_as_primary } = {}) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/versions/${versionId}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ version_name, set_as_primary }),
  });
  return parseResponse(res);
}

export async function deleteBoqVersion(token, projectId, versionId) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/versions/${versionId}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 204) {
    const data = await res.json().catch(() => ({}));
    const error = new Error(data?.detail || "Unable to delete version");
    error.status = res.status;
    throw error;
  }
  return true;
}

export async function renameBoqBaseName(token, tableId, baseName, cascade = true) {
  const res = await fetch(apiUrl(`product-tables/${tableId}/base-name`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ base_name: baseName, cascade }),
  });
  return parseResponse(res);
}

// ─── BOQ SNAPSHOT HISTORY ────────────────────────────────────────────────────

export async function createBoqSnapshot(token, projectId, payload) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/snapshots`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function fetchBoqSnapshots(token, projectId, limit = 50, offset = 0) {
  const res = await fetch(
    apiUrl(`projects/${projectId}/boq/snapshots?limit=${limit}&offset=${offset}`),
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return parseResponse(res);
}

export async function fetchSnapshotRowDiff(token, projectId, a, b) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/snapshots/row-diff?a=${a}&b=${b}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function fetchRestorePreview(token, projectId, snapshotId) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/snapshots/${snapshotId}/preview-restore`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function exportProjectBoq(token, projectId) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/export`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseResponse(res);
}

export async function branchBoqSnapshot(token, projectId, snapshotId, versionName) {
  const res = await fetch(apiUrl(`projects/${projectId}/boq/snapshots/${snapshotId}/branch`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ version_name: versionName }),
  });
  return parseResponse(res);
}

export async function restoreBoqSnapshot(token, projectId, snapshotId) {
  const res = await fetch(
    apiUrl(`projects/${projectId}/boq/snapshots/${snapshotId}/restore`),
    { method: "POST", headers: { Authorization: `Bearer ${token}` } }
  );
  return parseResponse(res);
}

export async function compareBoqSnapshots(token, projectId, snapIdA, snapIdB) {
  const res = await fetch(
    apiUrl(`projects/${projectId}/boq/snapshots/compare?a=${snapIdA}&b=${snapIdB}`),
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return parseResponse(res);
}

// ── Approvals, comments, client links, price impact ──
const authJson = async (token, path, { method = "GET", body } = {}) => {
  const res = await fetch(apiUrl(path), {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 204) return {};
  return parseResponse(res);
};

export const fetchApprovals = (token, projectId) => authJson(token, `projects/${projectId}/boq/approvals`);
export const approveSnapshot = (token, projectId, snapshotId, note = "") =>
  authJson(token, `projects/${projectId}/boq/snapshots/${snapshotId}/approve`, { method: "POST", body: { note } });
export const revokeApproval = (token, projectId, versionId) =>
  authJson(token, `projects/${projectId}/boq/versions/${versionId}/revoke-approval`, { method: "POST" });

export const fetchComments = (token, projectId, versionId) =>
  authJson(token, `projects/${projectId}/boq/comments?version_id=${encodeURIComponent(versionId)}`);
export const createComment = (token, projectId, body) =>
  authJson(token, `projects/${projectId}/boq/comments`, { method: "POST", body });
export const resolveComment = (token, projectId, commentId, resolved) =>
  authJson(token, `projects/${projectId}/boq/comments/${commentId}`, { method: "PATCH", body: { resolved } });
export const deleteComment = (token, projectId, commentId) =>
  authJson(token, `projects/${projectId}/boq/comments/${commentId}`, { method: "DELETE" });

export const fetchShares = (token, projectId) => authJson(token, `projects/${projectId}/boq/shares`);
export const createShare = (token, projectId, snapshotId, body) =>
  authJson(token, `projects/${projectId}/boq/snapshots/${snapshotId}/share`, { method: "POST", body });
export const revokeShare = (token, projectId, shareId) =>
  authJson(token, `projects/${projectId}/boq/shares/${shareId}`, { method: "DELETE" });

export const fetchPriceImpact = (token, projectId, versionId) =>
  authJson(token, `projects/${projectId}/boq/price-impact${versionId ? `?version_id=${encodeURIComponent(versionId)}` : ""}`);
export const applyPriceImpact = (token, projectId, versionId, rowIds) =>
  authJson(token, `projects/${projectId}/boq/price-impact/apply`, {
    method: "POST",
    body: { version_id: versionId, row_ids: rowIds || null },
  });

export const fetchRfq = (token, projectId, versionId) =>
  authJson(token, `projects/${projectId}/boq/rfq${versionId ? `?version_id=${encodeURIComponent(versionId)}` : ""}`);
// focus = { sheet, label } of the BOQ row the user has selected; omitted clears it on the server.
export const sendPresence = (token, projectId, focus) =>
  authJson(token, `projects/${projectId}/presence`, { method: "POST", ...(focus ? { body: focus } : {}) });
// Who else is working right now, for every project the caller can see: { [projectId]: [{ user_id, name }] }
export const fetchPresenceOverview = (token) => authJson(token, "presence/online");

export const pruneSnapshots = (token, projectId) =>
  authJson(token, `projects/${projectId}/boq/snapshots/prune`, { method: "POST" });
